import React, { useEffect } from "react";

import type { Option } from "@storeblocks/select";
import { StyledAlert } from "App/global-style";
import { Select } from "Components/Form/Select";
import { useTexts } from "Components/TextContext";
import { CarCoverage } from "Store/interfaces/products/coverage";
import { TextLanguage } from "Store/texts/interfaces";
import { getError } from "Utils/productUtils";
import { useFormikContext } from "formik";
import { DEFAULT_DEDUCTIBLE_CAR } from "../../carConstants";
import { CarPriceFormValues } from "../interfaces";

interface Props {
  readonly texts: any;
  readonly lang: TextLanguage;
}

const EMPTY_OPTION = {
  label: "",
  value: "",
};
const getDeductibleOptions = (
  options: any,
  withEmptyPlaceholder = false,
): Option[] => {
  const deductibleOptions = [];

  if (withEmptyPlaceholder) {
    deductibleOptions.push(EMPTY_OPTION);
  }

  return deductibleOptions.concat(
    options.deductiblesNew.map(
      (option: { name: string; value: string }): Option => ({
        label: option.name,
        value: option.value,
      }),
    ),
  );
};

const DEDUCTIBLE_DRIVER_AND_PASSENGER_ACCIDENT = "";
const DEDUCTIBLE_REDUCED_OWN_DAMAGE = "8000";

export const Deductible = ({ texts }: Props): React.ReactElement => {
  const formProps = useFormikContext<CarPriceFormValues>();
  const { getText } = useTexts();

  const coverage = formProps.values.coverage;
  const deductible = formProps.values.deductible;
  const disabled = [
    CarCoverage.AUTO_DRIVER_AND_PASSENGER_ACCIDENT,
    CarCoverage.AUTO_REDUCED_OWN_DAMAGE,
  ].includes(coverage);

  useEffect(() => {
    const setDeductible = (value: string): void => {
      formProps.setFieldValue("deductible", value);
    };

    if (
      coverage === CarCoverage.AUTO_DRIVER_AND_PASSENGER_ACCIDENT &&
      deductible !== DEDUCTIBLE_DRIVER_AND_PASSENGER_ACCIDENT
    ) {
      setDeductible(DEDUCTIBLE_DRIVER_AND_PASSENGER_ACCIDENT);
    } else if (
      coverage === CarCoverage.AUTO_REDUCED_OWN_DAMAGE &&
      deductible !== DEDUCTIBLE_REDUCED_OWN_DAMAGE
    ) {
      setDeductible(DEDUCTIBLE_REDUCED_OWN_DAMAGE);
    } else if (!disabled && deductible === "") {
      setDeductible(DEFAULT_DEDUCTIBLE_CAR);
    }
  }, [coverage, deductible, disabled]);

  const deductibleOptions = getDeductibleOptions(
    texts.deductible.options,
    disabled,
  );

  return (
    <>
      <Select
        name="deductible"
        id="deductible"
        label={getText("carform.label.deductible")}
        hint={getText("carform.hint.deductible")}
        options={deductibleOptions}
        error={getError("deductible", formProps)}
        large
        disabled={disabled}
      />

      {coverage === CarCoverage.AUTO_DRIVER_AND_PASSENGER_ACCIDENT && (
        <StyledAlert
          type="success"
          title={getText("carform.alerttitle.deductible_liability")}
        />
      )}

      {coverage === CarCoverage.AUTO_REDUCED_OWN_DAMAGE && (
        <StyledAlert
          title={getText("carform.alerttitle.deductible_reduced-damage")}
        />
      )}
    </>
  );
};
